import type { TFunction } from 'i18next';

import { formatCurrency } from '@/app/ops/helpers/formatters';
import type { TComoCalculamosSecao } from '@/components/explica/comoCalculamos';
import { fractionToPercent } from '@/components/explica/veredito';
import type { OpsSummary } from '@/domain/ops/interfaces/ops';

/** The numbers of the day, each one with the section that explains it in full. */
export type THomeNumero =
  | 'riscoFinanceiro'
  | 'entregasVencidas'
  | 'travadasMateriaPrima'
  | 'desvios'
  | 'semTempoPorPeca';

export interface IHomeExplicacao {
  numero: THomeNumero;
  secao: TComoCalculamosSecao;
  passos: Array<string>;
}

interface IBuildHomeExplicacoesParams {
  t: TFunction;
  resumo: OpsSummary | null;
  /** Pace deviations as the queue counted them. */
  desvios: number | null;
  /** The tolerated deviation in force, as a fraction; `null` until the settings arrive. */
  tolerancia: number | null;
  /** The minimum sample a deviation needs before it opens a queue item. */
  amostraMinima: number | null;
}

/**
 * What each number of the day counts, with the numbers of TODAY inside: the total it came
 * out of, and for the pace deviation the two parameters that decide when it lights up.
 * A source that did not answer has no explanation to give, so its number is left out.
 */
export function buildHomeExplicacoes({
  t,
  resumo,
  desvios,
  tolerancia,
  amostraMinima,
}: IBuildHomeExplicacoesParams): Array<IHomeExplicacao> {
  const explicacoes: Array<IHomeExplicacao> = [];
  const total = resumo?.totalOps ?? 0;

  if (resumo !== null) {
    // The breakdown rides in the (i) because the card has room for one number only, and
    // the total alone does not say whether it is a phone call today or a replan.
    const risco = resumo.riscoFinanceiro;
    explicacoes.push({
      numero: 'riscoFinanceiro',
      secao: 'prazo',
      passos: [
        t('home.explica.riscoFinanceiro.oque'),
        t('home.explica.riscoFinanceiro.hoje', {
          valor: formatCurrency(risco?.valor ?? 0),
          ops: risco?.ops ?? 0,
          total: formatCurrency(resumo.totalFaturamento),
        }),
        // Este é o número de CAPA do atraso: a frase diz em que os outros dois diferem.
        t('home.explica.riscoFinanceiro.contraOutros'),
        t('home.explica.riscoFinanceiro.abertura', {
          vencido: formatCurrency(risco?.jaVencido ?? 0),
          atrasar: formatCurrency(risco?.vaiAtrasar ?? 0),
          estimado: formatCurrency(risco?.comTempoEstimado ?? 0),
        }),
      ],
    });
    explicacoes.push({
      numero: 'entregasVencidas',
      secao: 'prazo',
      passos: [
        t('home.explica.entregasVencidas.oque'),
        t('home.explica.entregasVencidas.hoje', { valor: resumo.entregasVencidas, total }),
      ],
    });
    explicacoes.push({
      numero: 'travadasMateriaPrima',
      secao: 'fila',
      passos: [
        t('home.explica.travadasMateriaPrima.oque'),
        t('home.explica.travadasMateriaPrima.hoje', { valor: resumo.travadasMateriaPrima, total }),
        // Why the Fila shows a smaller number for what looks like the same question.
        t('home.explica.travadasMateriaPrima.contraFila'),
      ],
    });
  }

  if (desvios !== null) {
    // The two parameters come from Settings, never from the text: without them the
    // explanation simply does not claim a threshold.
    const quando =
      tolerancia === null || amostraMinima === null
        ? []
        : [
            t('home.explica.desvios.quando', {
              tolerancia: fractionToPercent(tolerancia),
              minimo: amostraMinima,
            }),
          ];
    explicacoes.push({
      numero: 'desvios',
      secao: 'fila',
      passos: [t('home.explica.desvios.oque'), ...quando, t('home.explica.desvios.hoje', { valor: desvios })],
    });
  }

  if (resumo !== null) {
    explicacoes.push({
      numero: 'semTempoPorPeca',
      secao: 'fontes',
      passos: [
        t('home.explica.semTempoPorPeca.oque'),
        t('home.explica.semTempoPorPeca.hoje', { valor: resumo.semTempoPorPeca, total }),
      ],
    });
  }

  return explicacoes;
}

/**
 * The same explanation as ONE text, for the (i) that lives on the card itself. The steps
 * read as a short paragraph because a tooltip has no room for a numbered list, and a
 * number whose source did not answer has no entry: its card is not on screen either.
 */
export function buildHomeExplicacaoPorNumero(params: IBuildHomeExplicacoesParams): Map<THomeNumero, string> {
  return new Map(buildHomeExplicacoes(params).map((explicacao) => [explicacao.numero, explicacao.passos.join(' ')]));
}
